/**
 * Her seat in the client (spec §3, §8): owns the MatchState, seals her action
 * the moment a turn opens, reveals it when the player commits, learns when
 * the match ends. Her thread is only ever read through the engine's `view`,
 * so there is no code path from a sealed action to the board (G8).
 */
import {
  ariadneLearn,
  ariadneSeed,
  ariadneTurn,
  createMatch,
  logRatios,
  matchVerdict,
  ready,
  resolve,
  seatOf,
  seededRandom,
  submit,
  view,
  type Action,
  type AriadneState,
  type GraphData,
  type MatchState,
  type MatchVerdict,
  type SeatResult,
  type SeatStatus,
} from "@synapse/engine";

import { getLoadedGraphIdentity } from "../services/dataLoader";
import { loadAriadneAssets, type AriadneAssets } from "./assets";
import { loadState } from "./persist";

export const PC = "you";
export const NPC = "ariadne";
/** The report's aiModel for a game she played (legacy key, Ruling R9). */
export const ARIADNE_MODEL = "ariadne-v2";

export interface SeatSave {
  match: MatchState;
  /** RNG draws made so far; a resume re-seeds and skips this many. */
  draws: number;
  /** She is following the player (the tutorial, spec §3.1a). */
  follow?: true;
}

export interface Reveal {
  thread: string[];
  status: SeatStatus;
  over: boolean;
}

export interface MatchOutcome {
  verdict: MatchVerdict;
  you: SeatResult;
  her: SeatResult;
  thread: string[];
  status: SeatStatus;
  /** Her learned-games counter after this game. */
  games: number;
  hash: string;
  /** She followed the player's every action (the tutorial): no race, no verdict. */
  followed?: true;
}

export interface Loaded {
  assets: AriadneAssets;
  state: AriadneState;
}

export type Loader = (graph: GraphData) => Promise<Loaded>;

type Pair = { start: string; target: string };

const defaultLoader: Loader = async (graph) => {
  const assets = await loadAriadneAssets(
    graph,
    getLoadedGraphIdentity()?.vocabHash ?? null,
  );
  const state = await loadState(assets.ids, assets.n);
  return { assets, state };
};

export class AriadneSeat {
  private graph: GraphData | null = null;
  private loading: Promise<Loaded | null> | null = null;
  private loaded: Loaded | null = null;
  private match: MatchState | null = null;
  private draws = 0;
  private rng: (() => number) | null = null;
  private lr: number[][] | null = null;
  /** A game resumed from a save that had no match: she sits this one out. */
  private solo = false;
  /** Following the player (the tutorial, spec §3.1a): her action is theirs. */
  private follow = false;

  constructor(private readonly loader: Loader = defaultLoader) {}

  /** Load her assets and state for this graph once; null (and one warning) when she cannot play on it. */
  prepare(graph: GraphData): Promise<Loaded | null> {
    if (this.graph !== graph || !this.loading) {
      this.graph = graph;
      this.loaded = null;
      this.abandon();
      this.loading = this.loader(graph).then(
        (l) => {
          this.loaded = l;
          return l;
        },
        (e: unknown) => {
          console.warn(
            "ariadne: not playing on this graph:",
            e instanceof Error ? e.message : e,
          );
          return null;
        },
      );
    }
    return this.loading;
  }

  /** Forget assets, state and match (a graph change, a reset, or a test). */
  reset(): void {
    this.graph = null;
    this.loading = null;
    this.loaded = null;
    this.abandon();
  }

  isActive(): boolean {
    return this.loaded !== null;
  }

  games(): number | null {
    return this.loaded ? this.loaded.state.games : null;
  }

  /** Drop the open match without learning (a new game started over it). */
  abandon(): void {
    this.match = null;
    this.rng = null;
    this.lr = null;
    this.draws = 0;
    this.solo = false;
    this.follow = false;
  }

  /** After a reset or a carry-over persisted elsewhere. */
  replaceState(state: AriadneState): void {
    if (this.loaded) this.loaded = { ...this.loaded, state };
  }

  private seedRng(state: AriadneState, pair: Pair, skip: number): void {
    const base = seededRandom(ariadneSeed(state, pair));
    for (let i = 0; i < skip; i++) base();
    this.draws = skip;
    this.rng = () => {
      this.draws++;
      return base();
    };
  }

  /**
   * Spec §3.1: her action is computed when the turn opens and held sealed by
   * the match. Following (§3.1a), nothing is sealed: her action is the
   * player's, submitted at commit.
   */
  private seal(): void {
    const m = this.match;
    const l = this.loaded;
    if (this.follow || !m || !l || !this.rng || !this.lr || m.status !== "open")
      return;
    const her = seatOf(m, NPC);
    if (her.status !== "playing" || NPC in m.sealed) return;
    const d = ariadneTurn(l.assets.deps, l.state, her.game, this.lr, this.rng);
    const r = submit(m, NPC, d.action);
    if (!r.ok)
      throw new Error(`ariadne: could not seal her action: ${r.error}`);
    this.match = r.match;
  }

  private reveal(): Reveal {
    const m = this.match as MatchState;
    const graph = (this.loaded as Loaded).assets.deps.graph;
    const her = view(graph, m, PC).others[0];
    return { thread: her.path, status: her.status, over: m.status === "over" };
  }

  async begin(
    graph: GraphData,
    pair: Pair,
    follow = false,
  ): Promise<Reveal | null> {
    const l = await this.prepare(graph);
    if (!l) return null;
    this.abandon();
    this.follow = follow;
    this.match = createMatch(graph, pair, [
      { id: PC, role: "pc" },
      { id: NPC, role: "npc" },
    ]);
    this.lr = logRatios(l.state);
    this.seedRng(l.state, pair, 0);
    this.seal();
    return this.reveal();
  }

  /** Continue a saved match; with no save the game is played solo (a save from before she existed). */
  async resume(
    graph: GraphData,
    save: SeatSave | null,
  ): Promise<Reveal | null> {
    const l = await this.prepare(graph);
    this.abandon();
    if (!l) return null;
    if (!save) {
      this.solo = true;
      return null;
    }
    try {
      const m = save.match;
      const shape =
        m &&
        typeof m.start === "string" &&
        typeof m.target === "string" &&
        Array.isArray(m.seats) &&
        m.seats.length === 2 &&
        m.seats[0]?.id === PC &&
        m.seats[1]?.id === NPC &&
        m.sealed !== null &&
        typeof m.sealed === "object" &&
        Number.isInteger(save.draws) &&
        save.draws >= 0;
      if (!shape) throw new Error("saved match has the wrong shape");
      this.match = m;
      this.follow = save.follow === true;
      this.lr = logRatios(l.state);
      this.seedRng(l.state, { start: m.start, target: m.target }, save.draws);
      this.seal();
      return this.reveal();
    } catch (e) {
      // A save from another release: she sits this game out rather than
      // failing app start.
      console.warn(
        "ariadne: could not resume the saved match; playing solo:",
        e instanceof Error ? e.message : e,
      );
      this.abandon();
      this.solo = true;
      return null;
    }
  }

  /**
   * The player's turn-t action: seal it, reveal and apply both, open turn t+1.
   * `follow` (only read when a match begins here) makes her follow (§3.1a).
   */
  async commit(
    graph: GraphData,
    pair: Pair,
    action: Action,
    opts: { follow?: boolean } = {},
  ): Promise<Reveal | null> {
    if (this.solo) return null;
    const m = this.match;
    const samePair =
      m !== null && m.start === pair.start && m.target === pair.target;
    if (!samePair || m.status !== "open") {
      if (samePair && m.status === "over") return this.reveal();
      if (!(await this.begin(graph, pair, !!opts.follow))) return null;
    }
    const r1 = submit(this.match as MatchState, PC, action);
    if (!r1.ok) {
      if (r1.error === "match_over") return this.reveal();
      throw new Error(
        `ariadne: could not seal the player's action: ${r1.error}`,
      );
    }
    this.match = r1.match;
    if (this.follow) {
      // Following: her action is the player's, so both seats walk one path.
      const r2 = submit(this.match, NPC, action);
      if (r2.ok) this.match = r2.match;
    }
    if (!ready(this.match)) {
      throw new Error("ariadne: the turn is not ready to resolve");
    }
    this.match = resolve(graph, this.match);
    this.seal();
    return this.reveal();
  }

  /**
   * When the match is over: learn (spec §4.5), judge (spec §3.2), and let the
   * match go. With `learn` false she is judged but taught nothing and
   * `learned` is her state as it was. A followed match (the tutorial, §3.1a)
   * teaches her the player's route and is marked `followed`.
   */
  end(
    learn = true,
  ): { outcome: MatchOutcome; learned: AriadneState; taught: boolean } | null {
    const m = this.match;
    const l = this.loaded;
    if (!m || !l || m.status !== "over") return null;
    const graph = l.assets.deps.graph;
    const learned = learn
      ? ariadneLearn(l.assets.deps, l.state, seatOf(m, PC))
      : l.state;
    this.loaded = { ...l, state: learned };
    const v = matchVerdict(graph, m, PC, NPC);
    const her = view(graph, m, PC).others[0];
    const followed = this.follow;
    this.abandon();
    return {
      outcome: {
        verdict: v.verdict,
        you: v.pc,
        her: v.npc,
        thread: her.path,
        status: her.status,
        games: learned.games,
        hash: learned.ariadne_hash,
        ...(followed ? { followed: true as const } : {}),
      },
      learned,
      taught: learn,
    };
  }

  snapshot(): SeatSave | null {
    if (!this.match) return null;
    return {
      match: this.match,
      draws: this.draws,
      ...(this.follow ? { follow: true as const } : {}),
    };
  }

  thread(): string[] | null {
    return this.match && this.loaded ? this.reveal().thread : null;
  }

  status(): SeatStatus | null {
    return this.match && this.loaded ? this.reveal().status : null;
  }
}

export const ariadneSeat = new AriadneSeat();
